import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FavoriteButton from "@/components/feature/FavoriteButton";
import { useSEO } from "@/hooks/useSEO";
import { getRelated, getSiteById, formatVisits, formatDate } from "@/lib/navData";
import { getTone } from "@/lib/tone";

export default function DetailPage() {
  const { siteId } = useParams();
  const site = getSiteById(siteId ?? "");

  useSEO(
    site ? `${site.name}-详情页-51网址导航` : "网址详情 - 51网址导航",
    site ? site.description : "查看网址详情、功能介绍与相关推荐。",
    `/detail/${siteId ?? ""}`,
  );

  if (!site) {
    return (
      <div className="max-w-page mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-link-unlink"
          title="该网址信息未找到或已下架"
          description="您访问的网址信息不存在，可能已下架或链接有误，请返回首页浏览其他收录网址。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const tone = getTone(site.tone);
  const related = getRelated(site, 5);

  return (
    <div className="max-w-page mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: site.categoryName, to: `/category/${encodeURIComponent(site.categoryName)}` },
          { label: site.name },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-5">
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-background-200 bg-background-50 overflow-hidden">
            <div className={`h-24 md:h-28 bg-gradient-to-br ${tone.gradient} relative`}>
              <div className="absolute top-4 left-5 w-14 h-14 rounded-full bg-background-50/15" />
              <div className="absolute bottom-2 right-8 w-20 h-20 rounded-full bg-background-50/10" />
            </div>

            <div className="px-5 md:px-7 pb-7">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-9">
                <span
                  className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${tone.gradient} border-4 border-background-50 text-background-50 flex items-center justify-center font-heading font-extrabold text-2xl shrink-0`}
                >
                  {site.logoText}
                </span>
                <div className="flex-1 min-w-0 sm:pb-1">
                  <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
                    {site.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Link
                      to={`/category/${encodeURIComponent(site.categoryName)}`}
                      className={`text-xs px-2.5 py-1 rounded-full ${tone.softBg} ${tone.softText} cursor-pointer`}
                    >
                      {site.categoryName}
                    </Link>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600">
                      {site.subCategory}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-sm md:text-base text-foreground-600 mt-5 leading-relaxed">
                {site.description}
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <i className="ri-link text-foreground-400" />
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 break-all cursor-pointer"
                >
                  {site.url}
                </a>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-6">
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-base" />
                  快速访问官网
                </a>
                <FavoriteButton siteId={site.id} variant="full" />
                <Link
                  to={`/category/${encodeURIComponent(site.categoryName)}`}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-200 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  返回{site.categoryName}分类
                </Link>
              </div>
            </div>
          </div>

          <section className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
            <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                <i className="ri-file-text-line" />
              </span>
              网址介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mt-4 leading-loose">
              {site.detail}
            </p>

            <h3 className="font-heading font-semibold text-base text-foreground-950 mt-6">标签</h3>
            <div className="flex flex-wrap gap-2 mt-3">
              {site.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 px-4 py-3 rounded-lg bg-secondary-100/70 border border-secondary-200 text-xs text-secondary-900 leading-relaxed">
              <i className="ri-information-line mr-1" />
              本页网址信息为演示内容，点击快速访问将在新窗口打开目标网站，具体服务以官网为准。
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading font-bold text-base text-foreground-950 mb-4">网站信息</h2>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">所属分类</span>
                <Link
                  to={`/category/${encodeURIComponent(site.categoryName)}`}
                  className="text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {site.categoryName}
                </Link>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">子分类</span>
                <span className="text-foreground-900">{site.subCategory}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">收录时间</span>
                <span className="text-foreground-900">{formatDate(site.addedAt)}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">累计访问</span>
                <span className="text-foreground-900 flex items-center gap-1">
                  <i className="ri-fire-line text-accent-500" />
                  {formatVisits(site.visits)}
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 mt-6">
            <h2 className="font-heading font-bold text-base text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-links-line text-accent-500" />
              相关推荐
            </h2>
            <ul className="space-y-1">
              {related.map((item) => {
                const itemTone = getTone(item.tone);
                return (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span
                        className={`w-9 h-9 rounded-lg bg-gradient-to-br ${itemTone.gradient} text-background-50 flex items-center justify-center text-xs font-bold shrink-0`}
                      >
                        {item.logoText}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-900 truncate">{item.name}</span>
                        <span className="block text-xs text-foreground-400 truncate">{item.description}</span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300 shrink-0" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}